import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Lock, ArrowRight, Phone, ShieldCheck, UserPlus, LogIn, Globe, Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';
import axios from 'axios';
import { getSupportedCurrencies } from '@/utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [mode, setMode] = useState('signin'); // signin | signup
  const [step, setStep] = useState('phone'); // phone | otp
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, supportedLanguages, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const currencies = getSupportedCurrencies();

  const redirectTo = new URLSearchParams(location.search).get('redirect') || '/dashboard';

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!phone || phone.length < 6) {
      toast.error(t('errors.invalid_phone_number') || 'Enter a valid phone number');
      return;
    }
    if (mode === 'signup' && !name.trim()) {
      toast.error(t('errors.name_required') || 'Please enter your name');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-otp`, { phone_number: phone });
      toast.success(t('auth.otp.sent_message') || 'OTP sent! Use 123456 for testing');
      setStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.server_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error(t('errors.invalid_otp') || 'Enter 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        phone_number: phone,
        otp: otp,
        mode: mode,
      };
      if (mode === 'signup') {
        payload.name = name;
        payload.currency = currency;
        payload.language = selectedLanguage;
      }
      const res = await axios.post(`${API}/auth/verify-otp`, payload);

      // Apply language preference on signup
      if (mode === 'signup' && selectedLanguage !== 'en') {
        changeLanguage(selectedLanguage);
      }

      login(res.data.token, res.data.user);
      toast.success(mode === 'signup' ? t('auth.signup.success') : t('auth.login.success'));
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || t('errors.server_error');
      if (err.response?.status === 404 && mode === 'signin') {
        toast.error(t('errors.account_not_found'));
        setMode('signup');
        setStep('phone');
        setOtp('');
      } else if (err.response?.status === 409 && mode === 'signup') {
        toast.error(t('errors.account_exists'));
        setMode('signin');
        setStep('phone');
        setOtp('');
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="login-page">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 border border-white/30 rounded-sm" />
          <div className="absolute bottom-32 right-16 w-48 h-48 border border-white/20 rounded-sm rotate-12" />
          <div className="absolute top-1/2 left-1/3 w-32 h-32 border border-white/10 rounded-sm -rotate-6" />
        </div>
        <div className="relative z-10 flex flex-col justify-between p-16">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 backdrop-blur rounded-sm flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-2xl font-bold text-white tracking-tight">{t('common.app_name')}</span>
          </div>
          <div className="space-y-6">
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-white leading-none tracking-tight">
              {t('auth.branding.title_1')}<br />{t('auth.branding.title_2')}
            </h1>
            <p className="text-white/70 text-lg font-sans leading-relaxed max-w-sm">
              {t('auth.branding.description')}
            </p>
            <div className="flex items-center gap-6 text-white/50 text-sm font-medium uppercase tracking-wider">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                <span>{t('common.info')}</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-white/30" />
              <span>{t('loan.confirmation.success')}</span>
            </div>
          </div>
          <p className="text-white/30 text-xs font-mono">
            {t('disclaimer.text')}
          </p>
        </div>
      </div>

      {/* Right side - login/signup form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-6">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-4">
            <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center">
              <Lock className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-serif font-bold text-xl tracking-tight">{t('common.app_name')}</span>
          </div>

          {/* Mode tabs */}
          <div className="flex border border-border rounded-sm overflow-hidden">
            <button
              type="button"
              onClick={() => { setMode('signin'); setStep('phone'); setOtp(''); }}
              data-testid="signin-tab"
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold uppercase tracking-wider transition-colors ${mode === 'signin'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white text-muted-foreground hover:bg-muted/50'
                }`}
            >
              <LogIn className="w-4 h-4" />
              {t('auth.login.title')}
            </button>
            <button
              type="button"
              onClick={() => { setMode('signup'); setStep('phone'); setOtp(''); }}
              data-testid="signup-tab"
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold uppercase tracking-wider transition-colors ${mode === 'signup'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white text-muted-foreground hover:bg-muted/50'
                }`}
            >
              <UserPlus className="w-4 h-4" />
              {t('auth.signup.title')}
            </button>
          </div>

          <div className="space-y-1">
            <h2 className="font-serif text-3xl font-bold tracking-tight">
              {step === 'phone'
                ? mode === 'signin' ? t('auth.login.welcome_title') : t('auth.signup.welcome_title')
                : t('auth.otp.verify_title')}
            </h2>
            <p className="text-muted-foreground text-sm font-sans">
              {step === 'phone'
                ? mode === 'signin'
                  ? t('auth.login.description')
                  : t('auth.signup.description')
                : t('auth.otp.description')}
            </p>
          </div>

          {step === 'phone' ? (
            <form onSubmit={handleRequestOTP} className="space-y-4">
              {mode === 'signup' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium uppercase tracking-wider text-muted-foreground">{t('auth.signup.name_label')} *</label>
                  <Input
                    data-testid="name-input"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="h-12 text-lg rounded-sm border-input focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              )}
              <div className="space-y-2">
                <label className="text-sm font-medium uppercase tracking-wider text-muted-foreground">{t('auth.signup.phone_label')} *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="phone-input"
                    type="tel"
                    placeholder="+91 98765 43210"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    className="pl-10 h-12 text-lg rounded-sm border-input focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              {mode === 'signup' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <label className="text-sm font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                        <Languages className="w-3 h-3" /> {t('settings.language_label')}
                      </label>
                      <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                        <SelectTrigger className="h-10 rounded-sm text-sm" data-testid="signup-language-select">
                          <SelectValue>
                            {supportedLanguages[selectedLanguage]?.native || 'English'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(supportedLanguages).map(([code, lang]) => (
                            <SelectItem key={code} value={code}>
                              <span className="font-medium">{lang.native}</span>
                              <span className="text-xs text-muted-foreground ml-1">({lang.name})</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                        <Globe className="w-3 h-3" /> {t('settings.currency_label')}
                      </label>
                      <Select value={currency} onValueChange={setCurrency}>
                        <SelectTrigger className="h-10 rounded-sm text-sm" data-testid="signup-currency-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {currencies.map(c => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.symbol} {c.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </>
              )}

              <Button
                type="submit"
                data-testid="request-otp-button"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-sm uppercase tracking-wider font-semibold text-sm"
              >
                {loading ? t('common.loading') : t('auth.otp.send_button')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                {mode === 'signin' ? (
                  <>{t('auth.login.no_account')} <button type="button" onClick={() => { setMode('signup'); setOtp(''); }} className="text-primary font-semibold hover:underline" data-testid="switch-to-signup">{t('auth.signup.title')}</button></>
                ) : (
                  <>{t('auth.signup.has_account')} <button type="button" onClick={() => { setMode('signin'); setOtp(''); }} className="text-primary font-semibold hover:underline" data-testid="switch-to-signin">{t('auth.login.title')}</button></>
                )}
              </p>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div className="space-y-3">
                <label className="text-sm font-medium uppercase tracking-wider text-muted-foreground">{t('auth.otp.verify_title')}</label>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={setOtp}
                    data-testid="otp-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} className="w-12 h-14 text-xl border-input" />
                      <InputOTPSlot index={1} className="w-12 h-14 text-xl border-input" />
                      <InputOTPSlot index={2} className="w-12 h-14 text-xl border-input" />
                      <InputOTPSlot index={3} className="w-12 h-14 text-xl border-input" />
                      <InputOTPSlot index={4} className="w-12 h-14 text-xl border-input" />
                      <InputOTPSlot index={5} className="w-12 h-14 text-xl border-input" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <p className="text-xs text-center text-muted-foreground">
                  {t('auth.otp.sent_to')} {phone}
                </p>
              </div>
              <Button
                type="submit"
                data-testid="verify-otp-button"
                disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-sm uppercase tracking-wider font-semibold text-sm"
              >
                {loading ? t('common.loading') : mode === 'signup' ? t('auth.signup.title') : t('auth.otp.verify_button')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <button
                type="button"
                onClick={() => { setStep('phone'); setOtp(''); }}
                className="w-full text-sm text-muted-foreground hover:text-foreground text-center"
                data-testid="change-phone-button"
              >
                {t('auth.otp.change_phone')}
              </button>
            </form>
          )}

          <p className="text-xs text-muted-foreground/60 text-center" data-testid="login-disclaimer">
            {t('disclaimer.short_text')}
          </p>
        </div>
      </div>
    </div>
  );
}
